export const articleDiscountCode = {
  slug: 'usfans-discount-code',
  en: {
    title: 'USFans Discount Code: What to Check Before You Use One',
    description:
      'Learn how USFans discount codes work, how to verify them and what restrictions usually apply.',
    tag: 'Discounts',
    intro: [
      'Discount codes are popular because they promise savings, but not every code you find is active. Understanding how they work helps you use the right ones and skip the rest.',
      'This guide focuses on what to check before entering a code, so you spend less time on invalid offers.',
    ],
    sections: [
      {
        title: 'What a discount code is',
        paragraphs: [
          'A discount code is a text string, often a mix of letters and numbers, that reduces the price when entered at checkout. Codes are usually case-sensitive and must be entered exactly.',
          'Some codes apply to everything, while others only work on specific categories or above a minimum order.',
        ],
      },
      {
        title: 'Verifying a code',
        paragraphs: [
          'Prefer codes from official channels or clearly current listings. Mark unverified codes as unverified and never share payment details to activate a discount.',
          'Check the expiry date and any minimum-spend requirement before relying on a code.',
        ],
      },
      {
        title: 'Common restrictions',
        paragraphs: [
          'Codes often cannot be combined with other offers, may be limited to one use per account, or may exclude sale items.',
          'Read the fine print so you know exactly what a code applies to.',
        ],
      },
    ],
    faq: [
      { q: 'What is a USFans discount code?', a: 'A text code entered at checkout that reduces the price, usually case-sensitive and often limited by category or minimum order.' },
      { q: 'How do I verify a discount code?', a: 'Use codes from official or clearly current sources, check the expiry and minimum spend, and mark unverified codes as unverified.' },
      { q: 'Can I combine discount codes?', a: 'Usually not. Many codes cannot be combined with other offers and may be limited to one use per account.' },
    ],
  },
  pl: {
    title: 'USFans Discount Code: co sprawdzić przed użyciem',
    description:
      'Dowiedz się, jak działają kody rabatowe USFans, jak je weryfikować i jakie ograniczenia zwykle obowiązują.',
    tag: 'Rabaty',
    intro: [
      'Kody rabatowe są popularne, bo obiecują oszczędności, ale nie każdy znaleziony kod jest aktywny. Zrozumienie ich działania pomaga używać właściwych i pomijać resztę.',
      'Ten przewodnik skupia się na tym, co sprawdzić przed wpisaniem kodu, aby tracić mniej czasu na nieważne oferty.',
    ],
    sections: [
      {
        title: 'Czym jest kod rabatowy',
        paragraphs: [
          'Kod rabatowy to ciąg znaków, często mieszanka liter i cyfr, który obniża cenę po wpisaniu przy kasie. Kody zwykle rozróżniają wielkość liter i trzeba je wpisać dokładnie.',
          'Niektóre kody dotyczą wszystkiego, a inne działają tylko na konkretne kategorie lub powyżej minimalnego zamówienia.',
        ],
      },
      {
        title: 'Weryfikowanie kodu',
        paragraphs: [
          'Wybieraj kody z oficjalnych kanałów lub wyraźnie aktualnych zestawień. Niezweryfikowane kody oznaczaj jako niezweryfikowane i nigdy nie udostępniaj danych płatności, aby aktywować zniżkę.',
          'Sprawdź datę ważności i ewentualny minimalny próg zamówienia, zanim polegasz na kodzie.',
        ],
      },
      {
        title: 'Typowe ograniczenia',
        paragraphs: [
          'Kody często nie łączą się z innymi ofertami, mogą być ograniczone do jednego użycia na konto lub wykluczać produkty przecenione.',
          'Czytaj drobny druk, aby dokładnie wiedzieć, czego dotyczy kod.',
        ],
      },
    ],
    faq: [
      { q: 'Czym jest kod rabatowy USFans?', a: 'To kod tekstowy wpisywany przy kasie, który obniża cenę, zwykle rozróżniający wielkość liter i często ograniczony kategorią lub minimalnym zamówieniem.' },
      { q: 'Jak zweryfikować kod rabatowy?', a: 'Korzystaj z kodów z oficjalnych lub wyraźnie aktualnych źródeł, sprawdź ważność i minimalne zamówienie oraz oznaczaj niezweryfikowane kody.' },
      { q: 'Czy mogę łączyć kody rabatowe?', a: 'Zwykle nie. Wiele kodów nie łączy się z innymi ofertami i może być ograniczonych do jednego użycia na konto.' },
    ],
  },
  es: {
    title: 'Código de descuento de USFans: qué revisar antes de usarlo',
    description:
      'Aprende cómo funcionan los códigos de descuento de USFans, cómo verificarlos y qué restricciones suelen aplicar.',
    tag: 'Descuentos',
    intro: [
      'Los códigos de descuento son populares porque prometen ahorro, pero no todos los códigos que encuentras están activos. Entender cómo funcionan te ayuda a usar los correctos y descartar el resto.',
      'Esta guía se centra en qué revisar antes de introducir un código, para perder menos tiempo con ofertas inválidas.',
    ],
    sections: [
      {
        title: 'Qué es un código de descuento',
        paragraphs: [
          'Un código de descuento es una cadena de texto, a menudo mezcla de letras y números, que reduce el precio al introducirlo al pagar. Suele distinguir mayúsculas y debe escribirse exactamente.',
          'Algunos códigos aplican a todo, mientras que otros solo funcionan en categorías concretas o por encima de un pedido mínimo.',
        ],
      },
      {
        title: 'Verificar un código',
        paragraphs: [
          'Prefiere códigos de canales oficiales o listados claramente actuales. Marca los códigos sin verificar como tales y nunca compartas datos de pago para activar un descuento.',
          'Revisa la fecha de caducidad y cualquier mínimo de compra antes de confiar en un código.',
        ],
      },
      {
        title: 'Restricciones comunes',
        paragraphs: [
          'Los códigos a menudo no se combinan con otras ofertas, pueden limitarse a un uso por cuenta o excluir artículos en rebaja.',
          'Lee la letra pequeña para saber exactamente a qué aplica un código.',
        ],
      },
    ],
    faq: [
      { q: '¿Qué es un código de descuento de USFans?', a: 'Un código de texto que se introduce al pagar y reduce el precio, normalmente con distinción de mayúsculas y a menudo limitado por categoría o pedido mínimo.' },
      { q: '¿Cómo verifico un código de descuento?', a: 'Usa códigos de fuentes oficiales o claramente actuales, revisa la caducidad y el mínimo de compra, y marca los no verificados como tales.' },
      { q: '¿Puedo combinar códigos de descuento?', a: 'Normalmente no. Muchos códigos no se combinan con otras ofertas y pueden limitarse a un uso por cuenta.' },
    ],
  },
  fr: {
    title: 'Code promo USFans : ce qu’il faut vérifier avant de l’utiliser',
    description:
      'Découvrez comment fonctionnent les codes promo USFans, comment les vérifier et quelles restrictions s’appliquent généralement.',
    tag: 'Réductions',
    intro: [
      'Les codes promo sont populaires car ils promettent des économies, mais tous les codes trouvés ne sont pas actifs. Comprendre leur fonctionnement aide à utiliser les bons et à ignorer les autres.',
      'Ce guide se concentre sur ce qu’il faut vérifier avant de saisir un code, pour perdre moins de temps avec des offres invalides.',
    ],
    sections: [
      {
        title: 'Qu’est-ce qu’un code promo',
        paragraphs: [
          'Un code promo est une chaîne de caractères, souvent un mélange de lettres et de chiffres, qui réduit le prix une fois saisie au paiement. Il est généralement sensible à la casse.',
          'Certains codes s’appliquent à tout, d’autres seulement à certaines catégories ou au-delà d’un montant minimum.',
        ],
      },
      {
        title: 'Vérifier un code',
        paragraphs: [
          'Préférez les codes de canaux officiels ou de listes clairement à jour. Marquez les codes non vérifiés comme tels et ne partagez jamais vos données de paiement pour activer une réduction.',
          'Vérifiez la date d’expiration et tout montant minimum avant de vous fier à un code.',
        ],
      },
      {
        title: 'Restrictions courantes',
        paragraphs: [
          'Les codes ne se cumulent souvent pas avec d’autres offres, peuvent être limités à une utilisation par compte ou exclure les articles en promotion.',
          'Lisez les petits caractères pour savoir exactement à quoi s’applique un code.',
        ],
      },
    ],
    faq: [
      { q: 'Qu’est-ce qu’un code promo USFans ?', a: 'Un code texte saisi au paiement qui réduit le prix, généralement sensible à la casse et souvent limité par catégorie ou montant minimum.' },
      { q: 'Comment vérifier un code promo ?', a: 'Utilisez des codes de sources officielles ou clairement à jour, vérifiez l’expiration et le minimum, et marquez les non vérifiés comme tels.' },
      { q: 'Puis-je cumuler les codes promo ?', a: 'Généralement non. Beaucoup de codes ne se cumulent pas avec d’autres offres et peuvent être limités à une utilisation par compte.' },
    ],
  },
};